import {
  provideHttpClient,
  withInterceptorsFromDi,
  withFetch,
} from '@angular/common/http';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NgModule, inject } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { NgbActiveModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { provideHighlightOptions } from 'ngx-highlightjs';
import { MARKED_OPTIONS, MarkdownModule } from 'ngx-markdown';

import {
  ScreenTrackingService,
  UserTrackingService,
} from '@angular/fire/analytics';
import { provideAnalytics, getAnalytics } from '@angular/fire/analytics';
// Emulator hookups use the @angular/fire exports: @angular/fire bundles its own
// Firebase SDK copy, and it is that copy that creates the App, Auth, Firestore
// and Storage instances. Passing them to the app's top-level `firebase/*` copy
// fails its instanceof checks (#954).
import { provideAuth, connectAuthEmulator } from '@angular/fire/auth';
import {
  getAuth,
  initializeAuth,
  browserLocalPersistence,
  browserPopupRedirectResolver,
  inMemoryPersistence,
} from 'firebase/auth';
import { provideDatabase, getDatabase } from '@angular/fire/database';
import { provideFirestore, connectFirestoreEmulator, getFirestore } from '@angular/fire/firestore';
import { provideFunctions, getFunctions } from '@angular/fire/functions';
import { provideMessaging, getMessaging } from '@angular/fire/messaging';
import { providePerformance, getPerformance } from '@angular/fire/performance';
import {
  provideRemoteConfig,
  getRemoteConfig,
} from '@angular/fire/remote-config';
import { provideStorage, getStorage, connectStorageEmulator } from '@angular/fire/storage';
import { provideFirebaseApp, initializeApp, FirebaseApp } from '@angular/fire/app';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { environment } from '../environments/environment';
import { AuthService } from '@core/services/auth.service';
import { LoggingService } from '@core/services/logging.service';
import { APP_CONFIG_INITIALIZER } from './core/initializers/app-config.initializer';
import { FIREBASE_INITIALIZER } from './core/initializers/firebase.initializer';
import { FunctionsKeyInterceptor } from './core/interceptors/functions-key.interceptor';
import { AuthTokenInterceptor } from './core/interceptors/auth-token.interceptor';
import { NinjaAgentTenantInterceptor } from './core/interceptors/ninja-agent-tenant.interceptor';
import { CreditDepletionInterceptor } from './core/interceptors/credit-depletion.interceptor';
import { EmailVerificationRequiredInterceptor } from '@core/interceptors/email-verification-required.interceptor';
import { BackendResilienceInterceptor } from './core/interceptors/backend-resilience.interceptor';

/** Browser-only Firebase providers (Analytics, Performance, Messaging require browser APIs) */
function getBrowserOnlyFirebaseProviders() {
  if (typeof window === 'undefined') {
    return [];
  }
  return [
    ScreenTrackingService,
    UserTrackingService,
    provideAnalytics(() => getAnalytics()),
    provideMessaging(() => getMessaging()),
    providePerformance(() => getPerformance()),
  ];
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    RouterModule,
    NgbModule,
    // MarkdownModule.forRoot must live in the root module so its providers are
    // a single global singleton. Lazy feature modules import the bare
    // MarkdownModule (no forRoot) to get the directives without re-registering.
    MarkdownModule.forRoot({
      markedOptions: {
        provide: MARKED_OPTIONS,
        useValue: {
          gfm: true,
          breaks: false,
          pedantic: false,
        },
      },
    }),
  ],
  providers: [
    NgbActiveModal,
    LoggingService,
    AuthService,
    { provide: HTTP_INTERCEPTORS, useClass: FunctionsKeyInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthTokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: NinjaAgentTenantInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: CreditDepletionInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: EmailVerificationRequiredInterceptor, multi: true },
    // Registered last → innermost in the chain, so it catches backend (Tenable /
    // Ninja-Agent-X) read failures first and degrades them to empty results.
    { provide: HTTP_INTERCEPTORS, useClass: BackendResilienceInterceptor, multi: true },
    APP_CONFIG_INITIALIZER,
    FIREBASE_INITIALIZER,
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    ...getBrowserOnlyFirebaseProviders(),
    provideAuth(() => {
      const firebaseApp = inject(FirebaseApp);
      const isBrowser = typeof window !== 'undefined';
      // Configure explicit browser persistence and popup/redirect resolver so
      // Firebase Auth works consistently across local, dev, and production hosts.
      let auth;
      try {
        auth = initializeAuth(firebaseApp, {
          persistence: isBrowser ? browserLocalPersistence : inMemoryPersistence,
          popupRedirectResolver: isBrowser ? browserPopupRedirectResolver : undefined,
        });
      } catch {
        // Auth was already initialized (e.g., SSR + browser double-init or re-render)
        auth = getAuth(firebaseApp);
      }
      if (environment.useFirebaseEmulators) {
        const { host, port } = environment.firebaseEmulators.auth;
        console.warn(`Connecting to Auth emulator at ${host}:${port}`);
        connectAuthEmulator(auth, `http://${host}:${port}`, { disableWarnings: true });
      }
      return auth;
    }),
    provideDatabase(() => getDatabase()),
    provideFirestore(() => {
      const firebaseApp = inject(FirebaseApp);
      const firestore = getFirestore(firebaseApp);
      if (environment.useFirebaseEmulators) {
        const { host, port } = environment.firebaseEmulators.firestore;
        console.warn(`Connecting to Firestore emulator at ${host}:${port}`);
        try {
          // No mockUserToken: requests must carry the signed-in auth-emulator
          // user's ID token so security rules see the real uid and claims.
          connectFirestoreEmulator(firestore, host, Number(port));
        } catch (error: unknown) {
          // Hot reload re-runs this factory on an instance that is already
          // connected (or already started); the first connection stays in effect.
          console.warn('Firestore emulator already connected:', (error as Error)?.message);
        }
      }
      return firestore;
    }),
    provideFunctions(() => getFunctions()),
    // Only provide messaging and performance in browser context
    ...(typeof window !== 'undefined' ? [
      provideMessaging(() => getMessaging()),
      providePerformance(() => getPerformance()),
    ] : []),
    provideRemoteConfig(() => getRemoteConfig()),
    provideStorage(() => {
      const storage = getStorage();
      if (environment.useFirebaseEmulators) {
        const { host, port } = environment.firebaseEmulators.storage;
        console.warn(`Connecting to Storage emulator at ${host}:${port}`);
        connectStorageEmulator(storage, host, port);
      }
      return storage;
    }),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideHighlightOptions({
      fullLibraryLoader: () => import('highlight.js'),
    }),
  ],
})
export class AppModule {}
